import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SubscriptionGate } from '@/components/SubscriptionGate';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from 'react-hot-toast';
import ParticleBackground from '@/components/ParticleBackground';
import LoadingFallback from '@/components/LoadingFallback';
import Sidebar from '@/components/Sidebar';
import CommandPalette from '@/components/CommandPalette';
import { MOBILE_BREAKPOINT } from '@/config/layout';
import { Moon, Sun } from 'lucide-react';
import "@/App.css";

// Lazy load all pages
import {
  LazyDashboard, LazyTopPicks, LazyDailyDigest,
  LazyStrategyAnalyzer, LazySentimentAnalysis, LazyBacktesting,
  LazyAlerts, LazyEnhancedAnalysis
} from '@/utils/lazyLoad';

const LazyPortfolioTracker = lazy(() => import('@/pages/PortfolioTracker'));
const LazyStockAnalysis = lazy(() => import('@/pages/StockAnalysis'));
const LazyEarningsCalendar = lazy(() => import('@/pages/EarningsCalendar'));
const LazyEarningsIntel = lazy(() => import('@/pages/EarningsIntel'));
const LazyScreener = lazy(() => import('@/pages/Screener'));
const LazyWatchlist = lazy(() => import('@/pages/Watchlist'));
const LazySectorHeatmap = lazy(() => import('@/pages/SectorHeatmap'));
const LazyResonance = lazy(() => import('@/pages/Resonance'));
const LazyStockComparison = lazy(() => import('@/pages/StockComparison'));
const LazyLearningDashboard = lazy(() => import('@/pages/LearningDashboard'));
const LazyProgress = lazy(() => import('@/pages/Progress'));
const LazyEarningsIPOCalendar = lazy(() => import('@/pages/EarningsIPOCalendar'));
const LazyAITradingAssistant = lazy(() => import('@/pages/AITradingAssistant'));
const LazyMemeStocks = lazy(() => import('@/pages/MemeStocks'));
const LazyPaperTrading = lazy(() => import('@/pages/PaperTrading'));
const LazyTournaments = lazy(() => import('@/pages/Tournaments'));
const LazySignalFusion = lazy(() => import('@/pages/SignalFusion'));
const LazyTradeReplay = lazy(() => import('@/pages/TradeReplay'));
const LazyStrategyMap = lazy(() => import('@/pages/StrategyMap'));
const LazyChartVision = lazy(() => import('@/pages/ChartVision'));

// Auth pages
const LazyLogin = lazy(() => import('@/pages/Login'));
const LazyRegister = lazy(() => import('@/pages/Register'));
const LazySubscribe = lazy(() => import('@/pages/Subscribe'));
const LazyManifesto = lazy(() => import('@/pages/Manifesto'));
const LazyReferral = lazy(() => import('@/pages/Referral'));
const LazyDiscoverFeed = lazy(() => import('@/pages/DiscoverFeed'));
const LazyOptionsFlow = lazy(() => import('@/pages/OptionsFlow'));
const LazySocialTrading = lazy(() => import('@/pages/SocialTrading'));
const LazyWeeklyDigest = lazy(() => import('@/pages/WeeklyDigest'));

// Daily AI Brief (Phase 1.5 task #28 — content flywheel, SEO-friendly, public)
const LazyBriefIndex = lazy(() => import('@/pages/BriefIndexPage'));
const LazyBriefPage = lazy(() => import('@/pages/BriefPage'));

// Your Brief — per-user personalized layer on top of the shared Daily Brief.
// Auth-gated inside the page (needs fio_session). The daily-ritual hook.
const LazyMyBrief = lazy(() => import('@/pages/MyBrief'));

// My Track Record — the Memory-layer keystone (Decision Journal scorecard)
const LazyScorecard = lazy(() => import('@/pages/Scorecard'));

// Stickiness swarm — Behavioral Mirror, Edge Radar, Crowd Whisper, Trader Card, Digital Twin
const LazyBiases = lazy(() => import('@/pages/Biases'));
const LazyRadar = lazy(() => import('@/pages/Radar'));
const LazyWhisper = lazy(() => import('@/pages/Whisper'));
const LazyTraderCard = lazy(() => import('@/pages/TraderCard'));
const LazyVerifyTraderCard = lazy(() => import('@/pages/VerifyTraderCard'));
const LazyReceipt = lazy(() => import('@/pages/Receipt')); // Moat 2: Sovereign Receipt per conversation
const LazyAdminCustomerHealth = lazy(() => import('@/pages/AdminCustomerHealth')); // v1.10: ORA health dashboard
const LazyAdminKbUnknowns = lazy(() => import('@/pages/AdminKbUnknowns')); // v1.11: tier-3 KB-miss queue
const LazySealedCockpitReceipt = lazy(() => import('@/pages/SealedCockpitReceipt')); // Phase B: Resonance Cockpit public verifier
import OraTopBar from '@/components/OraTopBar';                              // ORA v1: host/concierge top-bar affordance (summon)
import OraConcierge from '@/components/OraConcierge';                       // ORA v2 (2026-06-10): proactive floating presence
const LazyCalibration = lazy(() => import('@/pages/Calibration')); // Moat 5: Conviction Calibration Onboarding
const LazyAutopsy = lazy(() => import('@/pages/Autopsy')); // Moat 1: Edge Autopsy
const LazyPublicAutopsy = lazy(() => import('@/pages/PublicAutopsy')); // Moat 1: public render
const LazyPublicBattle = lazy(() => import('@/pages/PublicBattle')); // Moat 7: public battle
const LazySourceVerdict = lazy(() => import('@/pages/SourceVerdict')); // Moat 6: Source Verdict
const LazyPublicSourceProfile = lazy(() => import('@/pages/PublicSourceProfile')); // Moat 6: source profile
const LazyBattleLeaderboard = lazy(() => import('@/pages/BattleLeaderboard')); // Battle leaderboard
const LazyForesightScoreboard = lazy(() => import('@/pages/ForesightScoreboard')); // Moat 4: Foresight Bond scoreboard
const LazyTwin = lazy(() => import('@/pages/Twin'));
const LazyForesight = lazy(() => import('@/pages/Foresight'));

// Public landing page — WCAG 2.2 compliant hero with above-the-fold CTA
// for mobile (375x812). Wraps Dashboard internally so logged-in flow is
// preserved while unauthenticated visitors see a clear value prop + signup.
const LazyHomePage = lazy(() => import('@/pages/HomePage'));

// ARCC v0.1 — Sealed Ledger surfaces
const LazyCollisionMatrix = lazy(() => import('@/pages/CollisionMatrix'));
const LazyLedger = lazy(() => import('@/pages/Ledger'));
const LazyMintThesis = lazy(() => import('@/pages/MintThesis'));
const LazyThesisDetail = lazy(() => import('@/pages/ThesisDetail'));

// SRF v2 — Sovereign Review Fabric (SinaS code-review disruptor)
const LazySrf = lazy(() => import('@/pages/Srf'));

// 404 catch-all (Sovereign QA Mesh audit close-out)
const LazyNotFound = lazy(() => import('@/pages/NotFound'));

// Compliance surfaces — RiskDisclosure + Terms linked from BriefPage footer
const LazyRiskDisclosure = lazy(() => import('@/pages/RiskDisclosure'));
const LazyTerms = lazy(() => import('@/pages/Terms'));

// Demo — public-anonymous ARCC sample viewer (mint-once, serve-static)
const LazyDemo = lazy(() => import('@/pages/Demo'));

// Components that need to be immediately available
import LiveTicker from '@/components/LiveTicker';
import WelcomeGuide from '@/components/WelcomeGuide';

// Premium route wrapper — gates content behind subscription
function PremiumRoute({ feature, metered, children }) {
  return <SubscriptionGate feature={feature} metered={metered}>{children}</SubscriptionGate>;
}

// Layout for main app pages (with ticker, particles, welcome guide)
function AppLayout({ children }) {
  // ORA's top-bar affordance is summonable only — never pops itself. Per the
  // swarm: floating bubbles cheapen the brand from "character" to "widget" on
  // first contact. Text-link affordance preserves the three-character lane.
  const { theme, currentTheme, toggleTheme } = useTheme();
  // ORA v2 (2026-06-10): proactive concierge presence. OraTopBar remains
  // the canonical summon affordance in the chrome; OraConcierge layers a
  // floating bottom-right card that auto-surfaces 4.5s after page mount
  // with a context-aware recommendation. Dismissible per-page-per-session,
  // re-wakes after 60s idle. Commander directive: "she's supposed to be an
  // assistant, constantly finding ways to showcase skills to get users to
  // upgrade."
  const { isAuthenticated, user } = useAuth();
  const tier = user?.tier || (isAuthenticated ? "free" : "anon");
  return (
    <>
      <ParticleBackground />
      <WelcomeGuide />
      <LiveTicker />
      {/* γQC logo + account controls (UserMenu) now live in Sidebar's own
          header/footer — a standalone top-left mark here would visually
          collide with the sidebar once it renders, and stacking UserMenu into
          the already-crowded top-right chrome (theme toggle + OraTopBar) risks
          the same. */}
      <Sidebar />
      {/* Theme toggle — top right, parked LEFT of the "Ask ORA" affordance
          (OraTopBar sits at right:16, ~80px wide). Both used to be pinned to
          right:16 and overlapped — the toggle hid the ORA summon. */}
      <button onClick={() => toggleTheme()} style={{ position: 'fixed', top: 12, right: 108, zIndex: 40, background: theme.cardBg, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary, padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s' }} title={currentTheme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
        {currentTheme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
        {currentTheme === 'light' ? 'Dark' : 'Light'}
      </button>
      <OraTopBar theme={theme} />
      <OraConcierge theme={theme} isAuthenticated={isAuthenticated} tier={tier} />
      <CommandPalette />
      <div className="app-content-wrapper">
        {children}
      </div>
      <style>{`
        .app-content-wrapper {
          margin-left: var(--sidebar-width, 260px);
          transition: margin-left 0.2s ease;
        }
        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          .app-content-wrapper {
            margin-left: 0 !important;
          }
        }
      `}</style>
    </>
  );
}

// Clean layout for auth pages (no ticker/guide clutter)
function AuthLayout({ children }) {
  return <>{children}</>;
}

function AppContent() {
  return (
    <Suspense fallback={<LoadingFallback message="Loading page..." />}>
      <Routes>
        {/* v1.10 — ADMIN-ONLY customer health dashboard. NOT linked from nav.
            Auth via X-Admin-Token header gated server-side by ORA_ADMIN_TOKEN
            env var. Page has <meta robots="noindex"> to keep it out of
            search engines. Reach by typing the URL directly. */}
        <Route path="/admin/customer-health" element={<AuthLayout><LazyAdminCustomerHealth /></AuthLayout>} />
        <Route path="/admin/kb-unknowns" element={<AuthLayout><LazyAdminKbUnknowns /></AuthLayout>} />

        {/* PUBLIC verify route — no auth, no AppLayout (no ticker/welcome guide
            chrome). Each shared Trader Card URL is a marketing surface that
            sells GammaQC to visitors. */}
        <Route path="/verify/:cardId" element={<AuthLayout><LazyVerifyTraderCard /></AuthLayout>} />

        {/* Moat 2 — Sovereign Receipt per conversation. PUBLIC, no auth.
            Every Warren verdict mints one of these URLs at issuance time. */}
        <Route path="/r/:shareToken" element={<AuthLayout><LazyReceipt /></AuthLayout>} />

        {/* Phase B — Sealed Cockpit Receipt at /s/:shareToken. PUBLIC, no auth.
            Every cockpit-resolved verdict mints one of these: verdict + 10-seat
            council + 7-of-7 witness quorum (HMAC, Phase 1), verifiable offline. Backed by
            GET /api/cockpit-receipt/{share_token}. */}
        <Route path="/s/:shareToken" element={<AuthLayout><LazySealedCockpitReceipt /></AuthLayout>} />

        {/* Moat 1 — Public Edge Autopsy at /a/:shareToken. Every shared
            autopsy is a marketing surface that converts visitors. */}
        <Route path="/a/:shareToken" element={<AuthLayout><LazyPublicAutopsy /></AuthLayout>} />

        {/* Moat 7 — Public Daily Pick Battle at /b/:shareToken. */}
        <Route path="/b/:shareToken" element={<AuthLayout><LazyPublicBattle /></AuthLayout>} />

        {/* Battle leaderboard — public viral surface */}
        <Route path="/leaderboard" element={<AuthLayout><LazyBattleLeaderboard /></AuthLayout>} />

        {/* Moat 4 — Foresight Bond public scoreboard */}
        <Route path="/foresight-scoreboard" element={<AuthLayout><LazyForesightScoreboard /></AuthLayout>} />

        {/* Moat 6 — public source track record at /source/:platform/:handle */}
        <Route path="/source/:platform/:handle" element={<AuthLayout><LazyPublicSourceProfile /></AuthLayout>} />

        {/* Auth routes (clean layout) */}
        <Route path="/login" element={<AuthLayout><LazyLogin /></AuthLayout>} />
        <Route path="/register" element={<AuthLayout><LazyRegister /></AuthLayout>} />
        <Route path="/subscribe" element={<AuthLayout><LazySubscribe /></AuthLayout>} />
        {/* CLI users hit /pro from `gamma analyze` output — point at /subscribe */}
        <Route path="/pro" element={<Navigate to="/subscribe" replace />} />

        {/* ARCC v0.1 — Sealed Ledger surfaces */}
        <Route path="/collision" element={<AppLayout><LazyCollisionMatrix /></AppLayout>} />
        <Route path="/ledger" element={<AppLayout><LazyLedger /></AppLayout>} />
        {/* /ledger/new MUST be registered before /ledger/:thesisId so the
            literal segment wins over the dynamic match (React Router v6
            prefers static over dynamic, but the order is safest). */}
        <Route path="/ledger/new" element={<AppLayout><LazyMintThesis /></AppLayout>} />
        <Route path="/ledger/:thesisId" element={<AppLayout><LazyThesisDetail /></AppLayout>} />

        {/* SRF v2 — Sovereign Review Fabric (public marketing surface) */}
        <Route path="/srf" element={<AuthLayout><LazySrf /></AuthLayout>} />
        <Route path="/manifesto" element={<AuthLayout><LazyManifesto /></AuthLayout>} />
          <Route path="/referral" element={<AppLayout><LazyReferral /></AppLayout>} />

          {/* Free routes — visible to all (with app layout) */}
          {/* `/` uses HomePage (hero CTA + Dashboard body) so unauth visitors get
              a clear above-the-fold signup on mobile. /dashboard keeps the raw
              Dashboard for logged-in deep-links and back-compat. */}
          <Route path="/" element={<AppLayout><LazyHomePage /></AppLayout>} />
          <Route path="/dashboard" element={<AppLayout><LazyDashboard /></AppLayout>} />
          <Route path="/daily-digest" element={<AppLayout><LazyDailyDigest /></AppLayout>} />
          <Route path="/sector-heatmap" element={<AppLayout><LazySectorHeatmap /></AppLayout>} />
          <Route path="/chart" element={<AppLayout><LazyChartVision /></AppLayout>} />
          <Route path="/chart-vision" element={<AppLayout><LazyChartVision /></AppLayout>} />
          <Route path="/meme-stocks" element={<AppLayout><LazyMemeStocks /></AppLayout>} />
          <Route path="/learning" element={<AppLayout><LazyLearningDashboard /></AppLayout>} />
          <Route path="/progress" element={<AppLayout><LazyProgress /></AppLayout>} />
          <Route path="/earnings-calendar" element={<AppLayout><LazyEarningsCalendar /></AppLayout>} />
          <Route path="/earnings/:symbol" element={<AppLayout><LazyEarningsIntel /></AppLayout>} />
          {/* Consolidated 2026-07-06 into the Earnings Foresight hub */}
          <Route path="/earnings-ipo" element={<Navigate to="/earnings-calendar" replace />} />
          <Route path="/discover" element={<AppLayout><LazyDiscoverFeed /></AppLayout>} />

          {/* Daily AI Brief — public, no gate, SEO-friendly */}
          <Route path="/brief" element={<AppLayout><LazyBriefIndex /></AppLayout>} />
          <Route path="/brief/:date" element={<AppLayout><LazyBriefPage /></AppLayout>} />

          {/* Your Brief — personalized daily brief. Auth-gated inside the page. */}
          <Route path="/me/brief" element={<AppLayout><LazyMyBrief /></AppLayout>} />

          {/* My Track Record — the personal-data moat. Auth-gated inside the page. */}
          <Route path="/me/scorecard" element={<AppLayout><LazyScorecard /></AppLayout>} />

          {/* Moat 5 — Conviction Calibration Onboarding (Trader DNA quiz).
              Auth-gated inside the page; redirects to /login if unauthed. */}
          <Route path="/me/calibration" element={<AppLayout><LazyCalibration /></AppLayout>} />

          {/* Moat 1 — Edge Autopsy. Paste tickers + portfolio value, get
              $-quantified surgery in <90s. Auth-gated inside the page. */}
          <Route path="/me/autopsy" element={<AppLayout><LazyAutopsy /></AppLayout>} />

          {/* Moat 6 — Source Verdict. Paste any URL → Kim grades it +
              returns the source's historical track record. */}
          <Route path="/me/source-verdict" element={<AppLayout><LazySourceVerdict /></AppLayout>} />

          {/* Stickiness swarm — all auth-gated inside the page component */}
          <Route path="/me/biases" element={<AppLayout><LazyBiases /></AppLayout>} />
          <Route path="/me/radar" element={<AppLayout><LazyRadar /></AppLayout>} />
          <Route path="/whisper" element={<Navigate to="/earnings-calendar" replace />} />
          <Route path="/me/trader-card" element={<AppLayout><LazyTraderCard /></AppLayout>} />
          <Route path="/me/twin" element={<AppLayout><LazyTwin /></AppLayout>} />
          <Route path="/foresight" element={<Navigate to="/earnings-calendar" replace />} />

          {/* Premium routes — gated behind subscription (with app layout) */}
          <Route path="/options-flow" element={<AppLayout><PremiumRoute feature="Options Flow"><LazyOptionsFlow /></PremiumRoute></AppLayout>} />
          <Route path="/social" element={<AppLayout><PremiumRoute feature="Social Trading"><LazySocialTrading /></PremiumRoute></AppLayout>} />
          <Route path="/weekly-digest" element={<AppLayout><PremiumRoute feature="Weekly Digest"><LazyWeeklyDigest /></PremiumRoute></AppLayout>} />
          {/* Consolidated 2026-07-06: /analysis now serves the institutional
              war-room (was the thin StockAnalysis page). One analysis surface. */}
          <Route path="/analysis" element={<AppLayout><PremiumRoute feature="Stock Analysis" metered><LazyEnhancedAnalysis /></PremiumRoute></AppLayout>} />

          {/* Brief-ticker-link target: /analysis/AAPL etc. Behind same gate; reads :symbol from URL */}
          <Route path="/analysis/:symbol" element={<AppLayout><PremiumRoute feature="Stock Analysis" metered><LazyEnhancedAnalysis /></PremiumRoute></AppLayout>} />
          <Route path="/enhanced-analysis" element={<AppLayout><PremiumRoute feature="Enhanced Analysis"><LazyEnhancedAnalysis /></PremiumRoute></AppLayout>} />
          <Route path="/top-picks" element={<AppLayout><PremiumRoute feature="Top AI Picks"><LazyTopPicks /></PremiumRoute></AppLayout>} />
          <Route path="/screener" element={<AppLayout><PremiumRoute feature="Screener"><LazyScreener /></PremiumRoute></AppLayout>} />
          {/* Smart Screener merged into the unified Screener (2026-07-02) —
              old URL kept alive as a redirect so bookmarks/deep links work. */}
          <Route path="/smart-screener" element={<Navigate to="/screener" replace />} />
          <Route path="/strategy-analyzer" element={<AppLayout><PremiumRoute feature="Strategy Analyzer"><LazyStrategyAnalyzer /></PremiumRoute></AppLayout>} />
          <Route path="/watchlist" element={<AppLayout><PremiumRoute feature="Watchlist"><LazyWatchlist /></PremiumRoute></AppLayout>} />
          <Route path="/compare" element={<AppLayout><PremiumRoute feature="Stock Comparison"><LazyStockComparison /></PremiumRoute></AppLayout>} />
          <Route path="/alerts" element={<AppLayout><PremiumRoute feature="Smart Alerts"><LazyAlerts /></PremiumRoute></AppLayout>} />
          <Route path="/portfolio" element={<AppLayout><PremiumRoute feature="Portfolio Tracker"><LazyPortfolioTracker /></PremiumRoute></AppLayout>} />
          <Route path="/sentiment" element={<AppLayout><PremiumRoute feature="Sentiment Analysis"><LazySentimentAnalysis /></PremiumRoute></AppLayout>} />
          <Route path="/resonance" element={<Navigate to="/earnings-calendar" replace />} />
          <Route path="/backtest" element={<AppLayout><PremiumRoute feature="Backtesting"><LazyBacktesting /></PremiumRoute></AppLayout>} />
          <Route path="/ai-assistant" element={<AppLayout><PremiumRoute feature="AI Trading Assistant"><LazyAITradingAssistant /></PremiumRoute></AppLayout>} />
          {/* Paper Trading is FREE — it's the decision-capture surface that feeds
              the moat loop (Journal → Scorecard → Biases → Twin → Trader Card).
              Gating it starved free users of the data that builds switching cost.
              The paid tier gates advanced ANALYSIS (Stock Analysis, Top Picks,
              Screener, Strategy Analyzer, Options Flow), not the capture hook. */}
          <Route path="/paper-trading" element={<AppLayout><LazyPaperTrading /></AppLayout>} />
          {/* Tournaments is a PUBLIC, privacy-safe leaderboard — the competition/
              status hook that drives the daily-return loop. Ungated on purpose:
              gating it would hide the social proof that pulls free users back.
              "Your rank" needs a token but the board itself is open. */}
          <Route path="/tournaments" element={<AppLayout><LazyTournaments /></AppLayout>} />
          <Route path="/signal-fusion" element={<AppLayout><PremiumRoute feature="Signal Fusion"><LazySignalFusion /></PremiumRoute></AppLayout>} />
          <Route path="/trade-replay" element={<AppLayout><PremiumRoute feature="Trade Replay"><LazyTradeReplay /></PremiumRoute></AppLayout>} />
          <Route path="/strategy-map" element={<AppLayout><PremiumRoute feature="Strategy Map"><LazyStrategyMap /></PremiumRoute></AppLayout>} />

          {/* Compliance surfaces — linked from BriefPage + BriefIndexPage
              footers. AuthLayout because they're public (anyone can
              read them, no login required). */}
          <Route path="/risk" element={<AuthLayout><LazyRiskDisclosure /></AuthLayout>} />
          <Route path="/terms" element={<AuthLayout><LazyTerms /></AuthLayout>} />

          {/* Public demos — list + slug detail. Anonymous, no signup.
              Every receipt has is_simulation=true baked into the signed
              bytes (cryptographic lineage invariant). */}
          <Route path="/demo" element={<AuthLayout><LazyDemo /></AuthLayout>} />
          <Route path="/demo/:slug" element={<AuthLayout><LazyDemo /></AuthLayout>} />

          {/* Catch-all 404 — must be the LAST route. Sovereign QA Mesh
              audit (2026-06-14) found that typos / stale share links
              were producing blank screens with no CTA out. */}
          <Route path="*" element={<AppLayout><LazyNotFound /></AppLayout>} />
        </Routes>
      </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
        <div className="App">
          <Router>
            <AppContent />
          </Router>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
              },
            }}
          />
        </div>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
